/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect } from "react";
import Axios, { AxiosError } from "axios";
import { notification } from "antd";
import { StorageBox } from "./storage";

type Props = { children: any };

export const base = "https://api-v2.medpharma.care";
export const stage = "https://api-v2-staging.medpharma.care";
export const local = "http://localhost:9090";

// Read from the environment per build (set REACT_APP_API_URL in each
// deploy's env config - e.g. Netlify) instead of hand-editing/committing
// one of the constants above. Falls back to production (`base`), not
// localhost, so a missing env var fails safe rather than silently
// pointing a real build at a developer's own machine.
export const resolvedBaseURL = process.env.REACT_APP_API_URL || base;

const instance = Axios.create({
  baseURL: resolvedBaseURL,
  headers: { "Content-Type": "application/json" },
});

// Shared across all callers so concurrent 401s (e.g. several requests
// expiring at once) trigger exactly one /auth/refresh-token call instead of
// each firing its own - previously, every request past the first would
// retry with an already-rotated-out refresh token and force a logout.
let refreshPromise: Promise<{ token: string; refreshToken: string }> | null =
  null;

function refreshAuthToken(refreshToken: string) {
  if (!refreshPromise) {
    refreshPromise = instance
      .post("/auth/refresh-token", { refreshToken })
      .then((response) => response.data)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

const AxiosInterceptor = ({ children }: Props) => {
  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    instance.interceptors.request.use(
      async function (config) {
        // Get JWT token from localStorage
        const token = StorageBox.getToken();
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }

        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      },
    );

    instance.interceptors.response.use(
      (res) => {
        return res;
      },
      async (error: AxiosError) => {
        // Handle 401 Unauthorized - token expired or invalid
        if (error.response?.status === 401) {
          const refreshToken = StorageBox.getRefreshToken();
          if (refreshToken) {
            try {
              // Try to refresh the token (de-duplicated - see refreshAuthToken)
              const { token, refreshToken: newRefreshToken } =
                await refreshAuthToken(refreshToken);
              StorageBox.setToken(token);
              StorageBox.setRefreshToken(newRefreshToken);
              // Retry the original request
              if (error.config) {
                error.config.headers["Authorization"] = `Bearer ${token}`;
                return instance.request(error.config);
              }
            } catch (refreshError) {
              // Refresh failed, clear tokens and redirect to login
              StorageBox.clearStorage();
              window.location.href = "/sign-in";
            }
          } else {
            // No refresh token, redirect to login
            StorageBox.clearStorage();
            window.location.href = "/sign-in";
          }
        }
        return Promise.reject(error);
      },
    );

    instance.defaults.headers.get["Content-Type"] = "application/json";
    instance.defaults.headers.post["Content-Type"] = "application/json";
    instance.defaults.headers.patch["Content-Type"] = "application/json";

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {children}
      {contextHolder}
    </>
  );
};

export default instance;
export { AxiosInterceptor };
